import React, { useState, useEffect } from 'react';
import {
  Typography, Box, FormControlLabel, Checkbox, Tooltip
} from '@mui/material';
import isEqual from 'lodash/isEqual';
import InfoIcon from '@mui/icons-material/Info';
import { useTheme } from '@mui/material/styles';
import ProvisionTree from '../../Provision/components/ProvisionTree';
import provisionService from '../../../services/ProvisionService';
import courseService from '../../../services/CourseService';
import { useCourseState } from '../../../context/CourseContext';
import { getStyles } from '../styles';
import moment from 'moment';
import AccessSave from './AccessSave';
import testAheadService from '../../../services/TestAheadService';
import { AcademicYearDTO, Product, ProvisionedAuditLog } from '@beable/roster-backend-client-v1';
import { formatLogText } from '../../../util/formatLogText';
import { sortBy } from 'lodash';

export default function CourseAccess() {
    const theme = useTheme();
    const styles = getStyles(theme);
    const { site, schoolId, course, courseSet, testAheadCourse, accessSelections, product } = useCourseState();

    const [nodes, setNodes] = useState<any[]>([]);
    const [defaultNodes, setDefaultNodes] = useState<any[] | undefined>(undefined);
    const [savedNodes, setSavedNodes] = useState<any[]>([]);
    const [selectedNodes, setSelectedNodes] = useState<any[]>([]);
    const [currentAcademicYears, setCurrentAcademicYears] = useState<any[]>([]);
    const [futureAcademicYears, setFutureAcademicYears] = useState<any[]>([]);
    const [logInfo, setLogInfo] = useState<ProvisionedAuditLog | undefined>();
    const [isNodesLoading, setIsNodesLoading] = useState(false);

    useEffect(() => {
      const fetchNodeData = async () => {
        if (site && schoolId) {
          const orgId = (schoolId === 'ALL' ? undefined : schoolId);
          const data = await provisionService.getCourseCodes(site.id, orgId);
          setNodes(data);
        } else {
          setNodes([]);
        }
      };

      const getCourseAccessRules = async () => {
        if (site && (course || courseSet || testAheadCourse) && schoolId) {
          const orgId = (schoolId === 'ALL' ? undefined : schoolId);

          if (product?.code === Product.Exampal) {
            const data = await testAheadService.getTestAheadCourseAccess(
              site.id, testAheadCourse?.id ?? '', orgId,
            );
            setSavedNodes(data);
            return;
          }

          const data = await courseService.getCourseAccessRules(
              site.id, course?.courseId, courseSet?.id, orgId,
          );
          if (data) {
            // TODO: remove this check. Added for backwards compatability
            if (Array.isArray(data)) {
              setSavedNodes(data);
              return;
            }
            setSavedNodes(data.selections);
            setLogInfo(data.log)
          }
        } else {
          setSavedNodes([]);
        }
      };

      setIsNodesLoading(true);

      fetchNodeData();
      getCourseAccessRules();

      setIsNodesLoading(false);

    }, [site, course, courseSet, testAheadCourse, schoolId, product]);

    useEffect(() => {
      const fetchProvisionedSessions = async () => {
        if (site && schoolId) {
          const data = await provisionService.getAcademicYears(site.id);

          if (data) {
            //make sure its sorted date order ascending
            data.academicYears = sortBy(data.academicYears, (ay) => new Date(ay.startAt).getTime());

            const current: any[] = [];
            const future: any[] = [];
            data.academicYears.forEach((academicYear: AcademicYearDTO) => {
              if (moment(academicYear.startAt) <= moment()
                && moment(academicYear.endAt) >= moment()
              ) {
                current.push(academicYear);
              } else if (!future.length) {
                future.push(academicYear);
              }
            })

            setCurrentAcademicYears(current);
            setFutureAcademicYears(future);
          }
        }
      };

      fetchProvisionedSessions();
    }, [site, schoolId]);

    const handleDefaultsChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        const newNodes: any[] = [];

        nodes.forEach(node => {
          if (node.children) {
            node.children.forEach( (child: any) => {
              const lastKeyValue = child.value.split('|').pop();
              const keyValArray = lastKeyValue.split(':');
              if (keyValArray[0] === 'grade'
                && course && course.grades
                && course.grades.includes(keyValArray[1]))
              {
                newNodes.push(child.value);
              }
            });
          }
        });

        setDefaultNodes(newNodes);
      } else {
        setDefaultNodes(undefined);
      }
    }

    const getSelectedNodes = () => {
      if (defaultNodes) {
        return defaultNodes;
      }

      if (accessSelections) {
        return accessSelections;
      }

      return savedNodes;
    }

    return (
      <Box>
        <Typography variant='h6' sx={styles.stepContainer_header}>
          Course Access
          {logInfo && <Tooltip title={(formatLogText(logInfo))}>
            <InfoIcon color="primary" sx={styles.infoIcon} />
          </Tooltip>}
        </Typography>
        { course &&
          <Box sx={styles.stepContainer_section}>
            <FormControlLabel
              control={
                <Checkbox
                  onChange={handleDefaultsChange}
                  name='defaults'
                  color='primary'
                />
              }
              label='Use Defaults'
            />
          </Box>
        }
        <ProvisionTree
          nodes={nodes}
          preSelectedNodeValues={getSelectedNodes()}
          onChange={(newSelectedNodes) => {
            if (isEqual(newSelectedNodes.sort(), selectedNodes.sort())) {
              return;
            }
            setSelectedNodes(newSelectedNodes);
          }}
          rosterWarning={''}
          isLoading={isNodesLoading}
          filterGrades={courseSet && courseSet.grades ? courseSet.grades.split(',') : undefined}
        />
        <AccessSave
          savedNodes={savedNodes}
          selectedNodes={selectedNodes}
          currentAcademicYears={currentAcademicYears}
          futureAcademicYears={futureAcademicYears}
        />
      </Box>
    );
}